import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  Flex,
  LineChart,
  formatNumber,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';

export default function HistoricTabs() {
  const [activeDisplayOption, setActiveDisplayOption] = useState('total_usd');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const displayOptions = useMemo(
    () => [
      { key: 'total_usd', value: 'Liquidity' },
      { key: 'balance_sky', value: 'SKY' },
      { key: 'balance_usds', value: 'USDS' },
      { key: 'sky_owned', value: 'SKY in Treasury' },
      { key: 'sbe_total_usd', value: 'Impermanent Loss' }
    ],
    []
  );

  const [data, error] = useApi({
    url: '/sbe/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const yScaleValueFormatter = useMemo(() => {
    return (value) => {
      switch (activeDisplayOption) {
        case 'total_usd':
          return `$${formatNumber(value)}`;
        case 'sbe_total_usd':
          return `$${formatNumber(value)}`;
        default:
          return `${formatNumber(value)}`;
      }
    };
  }, [activeDisplayOption]);

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'total_usd':
          return `$${formatNumber(Number(value))}`;
        case 'sbe_total_usd':
          return `$${formatNumber(Number(value))}`;
        case 'balance_sky':
        case 'sky_owned':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('SKY'),
                iconSize: '3'
              }}
            />
          );
        case 'balance_usds':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('USDS'),
                iconSize: '3'
              }}
            />
          );
      }
    }

    return formatValue;
  }, [activeDisplayOption]);
  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <LineChart
          key={activeDisplayOption}
          data={data}
          datasets={[
            {
              xField: 'date',
              yField: activeDisplayOption
            }
          ]}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
